import { memo, useCallback, useState } from 'react';

import { PencilIcon, StickyNoteIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DeleteConfirmPopoverContent } from '@/components/atoms/DeleteConfirmPopover/DeleteConfirmPopoverContent';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor } from '@/components/ui/popover';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import { selectUserId } from '@/redux/user/selectors';
import type { Comment } from '@/shared/types/comments';
import { formatTimeOnly } from '@/util/dateUtils';

import { ACTIVITY_CARD_CLASS } from './activityCard';

type NoteEventProps = {
  comment: Comment;
  onEdit?: (comment: Comment) => void;
  onDelete?: (commentId: string) => void;
  readOnly?: boolean;
};

export const NoteEvent = memo(({ comment, onEdit, onDelete, readOnly = false }: NoteEventProps) => {
  const { t } = useTranslation();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const userId = useSelector(selectUserId);
  const { getMember } = useMembers();
  const author = getMember(comment.agent_id);
  const isOwner = comment.agent_id === userId;
  const isEdited = comment.updated !== comment.created;
  const authorName = author?.name ?? t('common.unknown');
  const showActions = !readOnly && isOwner;

  const handleDeleteConfirm = useCallback(() => {
    onDelete?.(comment.comment_id);
    setIsDeleteOpen(false);
  }, [onDelete, comment.comment_id]);

  return (
    <div className="flex justify-end px-6">
      <div className={cn(ACTIVITY_CARD_CLASS, 'group max-w-md min-w-80 bg-bg-warning-soft')}>
        <div className="flex items-center gap-4 py-0.5">
          <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <StickyNoteIcon size={16} className="shrink-0 self-center text-foreground-muted" />
            <InfoTooltip content={t('notes.note_from', { 0: authorName })} showArrow side="top">
              <span className="min-w-0 truncate text-sm leading-5 font-medium text-foreground">
                {t('notes.note_from', { 0: authorName })}
              </span>
            </InfoTooltip>
            <span className="shrink-0 text-xs leading-4 font-normal text-foreground-muted">
              {formatTimeOnly(comment.created)}
            </span>
            {isEdited && (
              <span className="shrink-0 text-xs leading-4 font-normal text-foreground-muted">
                ({t('notes.edited')})
              </span>
            )}
          </div>

          {showActions && (
            <Popover open={isDeleteOpen}>
              <div className="flex shrink-0 items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <Button
                  variant="ghost-neutral"
                  size="icon-xss"
                  onClick={() => onEdit?.(comment)}
                  aria-label={t('common.edit')}
                >
                  <PencilIcon size={16} />
                </Button>

                <PopoverAnchor asChild>
                  <Button
                    variant="ghost-neutral"
                    size="icon-xss"
                    onClick={() => setIsDeleteOpen(true)}
                    aria-label={t('common.delete')}
                    className="text-foreground-muted hover:text-destructive hover:[&_svg]:text-destructive"
                  >
                    <Trash2Icon size={16} />
                  </Button>
                </PopoverAnchor>
              </div>
              <DeleteConfirmPopoverContent
                title={t('notes.delete_title')}
                description={t('notes.delete_description')}
                onCancel={() => setIsDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                onPointerDownOutside={() => setIsDeleteOpen(false)}
                onEscapeKeyDown={() => setIsDeleteOpen(false)}
              />
            </Popover>
          )}
        </div>

        <p className="text-sm leading-6 break-words whitespace-pre-wrap text-foreground">
          {comment.text}
        </p>
      </div>
    </div>
  );
});

NoteEvent.displayName = 'NoteEvent';
